import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { PromisePool } from 'es6-promise-pool';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import { deleteCollectionsFromCollectionsList } from '../actions/Collections';
import {
  removeFilesFromFilesList,
  removeFilesFromSelectedFilesList,
} from '../actions/Files';

import { APIgetArchiveSummary } from '../api/Archive';
import {
  APIarchiveCollection,
  APIunarchiveCollection,
} from '../api/Collections';
import {
  APIarchiveFile,
  APIunarchiveFile,
} from '../api/Files';

import { sendTrackingEvent } from '../helpers';
import { getTitleFromSummary } from '../helpers/App';
import { addFlashMessage } from '../helpers/FlashMessages';

import ProcessingProgress from './ProcessingProgress';
import ProcessingSpinner from './ProcessingSpinner';

import ErrorBoundary from './ErrorBoundary';

class ArchiveModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      total: 0,
      updatedItems: 0,
      updateCollectionFiles: true,
      updateCollectionCollections: true,
      summary: {
        collections: 0,
        files: 0,
        size: 0,
      },
      summaryLoading: true,
      saving: false,
    };

    this.handleUpdate = this.handleUpdate.bind(this);
    this.handleCollectionFilesChange = this.handleCollectionFilesChange.bind(this);
  }

  componentDidMount() {
    this.getSummary();

    const { action } = this.props;
    sendTrackingEvent(`${action}-modal-open`);
  }

  getSummary() {
    const {
      action,
      collections,
      files,
    } = this.props;

    APIgetArchiveSummary(
      collections.map(c => c.uuid),
      files.map(f => f.uuid),
      action,
    ).then((response) => {
      this.setState({
        summary: {
          collections: response.data.collections,
          files: response.data.files,
          size: response.data.size,
        },
        summaryLoading: false,
      });
    });
  }

  handleUpdate() {
    const {
      files,
      collections,
      action,
      handleClose,
      deleteFilesFromList,
      deleteCollectionsFromList,
      afterCollectionArchived,
      afterCollectionsArchived,
      afterFileArchived,
      afterFilesArchived,
    } = this.props;
    const {
      summary,
      updateCollectionFiles,
      updateCollectionCollections,
    } = this.state;

    this.setState({
      saving: true,
      total: files.length + collections.length,
    });

    let counter = 0;
    let filesCounter = 0;
    let collectionsCounter = 0;
    const filesToRemove = [];
    const collectionsToRemove = [];

    const promiseProducerFiles = () => {
      if (filesCounter < files.length) {
        const item = files[filesCounter];
        filesCounter += 1;
        counter += 1;

        if (action === 'unarchive') {
          return APIunarchiveFile(item.uuid).then((response) => {
            if (response.data && response.data.uuid) {
              filesToRemove.push(item);
              afterFileArchived(response.data);
            }
            this.setState({ updatedItems: counter });
          });
        }
        return APIarchiveFile(item.uuid).then((response) => {
          if (response.data && response.data.uuid) {
            filesToRemove.push(item);
            afterFileArchived(response.data);
          }
          this.setState({ updatedItems: counter });
        });
      }
      return null;
    };

    const promiseProducerCollections = () => {
      if (collectionsCounter < collections.length) {
        const item = collections[collectionsCounter];
        counter += 1;
        collectionsCounter += 1;

        const params = {
          updateCollectionFiles,
          updateCollectionCollections,
        };
        if (action === 'unarchive') {
          return APIunarchiveCollection(item.uuid, params).then((response) => {
            if (response.data && response.data.uuid) {
              collectionsToRemove.push(item);
              afterCollectionArchived(response.data);
            }
            this.setState({ updatedItems: counter });
          });
        }
        return APIarchiveCollection(item.uuid, params).then((response) => {
          if (response.data && response.data.uuid) {
            collectionsToRemove.push(item);
            afterCollectionArchived(response.data);
          }
          this.setState({ updatedItems: counter });
        });
      }
      return null;
    };

    const poolFiles = new PromisePool(promiseProducerFiles, 2);
    const poolCollections = new PromisePool(promiseProducerCollections, 2);
    poolFiles.start().then(() => {
      afterFilesArchived(filesToRemove);
      poolCollections.start().then(() => {
        afterCollectionsArchived(collectionsToRemove);
        deleteFilesFromList(filesToRemove);
        deleteCollectionsFromList(collectionsToRemove);
        const { intl } = this.props;

        let button = null;
        if (collections.length === 1 && files.length === 0) {
          button = {
            url: action === 'archive' ? `/archive/collections/${collections[0].uuid}` : `/collections/${collections[0].uuid}`,
            text: intl.formatMessage({ defaultMessage: 'Open Collection' }),
          };
        }

        let text = '';
        if (action === 'archive') {
          text = intl.formatMessage({
            defaultMessage: '{title} have been archived',
          }, {
            title: getTitleFromSummary(summary, intl),
          });
        } else {
          text = intl.formatMessage({
            defaultMessage: '{title} have been unarchived',
          }, {
            title: getTitleFromSummary(summary, intl),
          });
        }
        addFlashMessage({
          type: 'success',
          text,
          button,
        });

        sendTrackingEvent(`${action}-complete`);

        handleClose();
      });
    });
  }

  handleCollectionFilesChange(e) {
    const { target } = e;
    this.setState({ updateCollectionFiles: target.checked });
    if (target.checked) {
      this.setState({ updateCollectionCollections: true });
    }
  }

  render() {
    const {
      intl,
      action,
      files,
      collections,
      handleClose,
    } = this.props;
    const {
      summary,
      updateCollectionFiles,
      summaryLoading,
      collectionsLoading,
      total,
      updatedItems,
      saving,
    } = this.state;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {action === 'archive' ? 'Archive' : 'Unarchive'}{' '}
        </ModalHeader>
        <ModalBody style={{ overflow: 'visible' }}>
          {files.length > 0 && (
            <>
              {action === 'archive' && (
                <p>
                  {intl.formatMessage({
                    defaultMessage: `You are about to archive {filesLength} {filesLength, plural, one {file} other {files}}.
                    {filesLength, plural, one {It} other {They}} will disappear from your main files list, all
                    Collections {filesLength, plural, one {it belongs} other {they belong}} to, and your main search.
                    You can unarchive {filesLength, plural, one {it} other {them}} at any time from the archive area.`,
                  }, { filesLength: files.length })}
                </p>
              )}
              {action === 'unarchive' && (
                <p>
                  {intl.formatMessage({
                    defaultMessage: `You are about to unarchive {filesLength} {filesLength, plural, one {file} other {files}}.
                    {filesLength, plural, one {It} other {They}} will appear back in your main files list, all
                    Collections {filesLength, plural, one {it} other {they}} belonged to, and your main search.`,
                  }, { filesLength: files.length })}
                </p>
              )}
            </>
          )}
          {collections.length > 0 && (
            <>
              {action === 'archive' && (
                <>
                  <p>
                    {intl.formatMessage({
                      defaultMessage: `You are about to archive {collectionsLength} {collectionsLength, plural, one {Collection} other {Collections}}.
                      {collectionsLength, plural, one {It} other {They}} will be removed from your Collections list and your main search.
                      You can unarchive {collectionsLength, plural, one {it} other {them}} at any time from the archive area.`,
                    }, { collectionsLength: collections.length })}
                  </p>
                  {summary.collections - collections.length > 0 && (
                    <p>
                      {intl.formatMessage({
                        defaultMessage: `{collectionsSummary} {collectionsSummary, plural, one {Collection} other {Collections}}
                        inside the {collectionsSummary, plural, one {Collection} other {Collections}} will be also archived.`,
                      }, { collectionsSummary: summary.collections - collections.length })}
                    </p>
                  )}
                </>
              )}
              {action === 'unarchive' && (
                <>
                  <p>
                    {intl.formatMessage({
                      defaultMessage: `You are about to unarchive {collectionsLength} {collectionsLength, plural, one {Collection} other {Collections}}.
                        {collectionsLength, plural, one {It} other {They}} will appear back in your Collections list and the main search.`,
                    }, { collectionsLength: collections.length })}
                  </p>
                  {summary.collections - collections.length > 0 && (
                    <p>
                      {intl.formatMessage({
                        defaultMessage: `{collectionsSummary} {collectionsSummary, plural, one {Collection} other {Collections}}
                        inside the {collectionsSummary, plural, one {Collection} other {Collections}} will be also unarchived.`,
                      }, { collectionsSummary: summary.collections - collections.length })}
                    </p>
                  )}
                </>
              )}
            </>
          )}
          {collections.length > 0 && (
            <Form className="archive-form">
              {summary.files - files.length > 0 && (
                <FormGroup check>
                  <Label for="collectionFiles" check>
                    <Input
                      type="checkbox"
                      id="collectionFiles"
                      className="input-check check-s form-check-input no-margin"
                      value="1"
                      disabled={saving || summaryLoading}
                      checked={updateCollectionFiles}
                      onChange={this.handleCollectionFilesChange}
                    />
                    {' '}
                    {
                      action === 'archive' ?
                        intl.formatMessage({
                          defaultMessage: `Also archive files inside the {collections, plural, one {Collection} other {Collections}}`,
                        }, { collections: summary.collections })
                        :
                        intl.formatMessage({
                          defaultMessage: `Also unarchive files inside the {collections, plural, one {Collection} other {Collections}}`,
                        }, { collections: summary.collections })
                    }
                    {!summaryLoading && (
                      <span> ({summary.files - files.length})</span>
                    )}
                  </Label>
                </FormGroup>
              )}
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving || collectionsLoading || summaryLoading}
              onClick={this.handleUpdate}
            >
              <ProcessingSpinner show={saving} />
              {action === 'archive' ? intl.formatMessage({ defaultMessage: 'Archive' }) : intl.formatMessage({ defaultMessage: 'Unarchive' })}
              <ProcessingProgress
                show={saving}
                progress={{ total, done: updatedItems }}
              />
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  deleteFilesFromList: (files) => {
    dispatch(removeFilesFromFilesList(files));
    dispatch(removeFilesFromSelectedFilesList(files));
  },
  deleteCollectionsFromList: (collections) => {
    dispatch(deleteCollectionsFromCollectionsList(collections));
  },
});

ArchiveModal.propTypes = {
  intl: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  deleteFilesFromList: PropTypes.func.isRequired,
  deleteCollectionsFromList: PropTypes.func.isRequired,
  action: PropTypes.string.isRequired,
  files: PropTypes.array,
  collections: PropTypes.array,
  afterCollectionArchived: PropTypes.func,
  afterCollectionsArchived: PropTypes.func,
  afterFileArchived: PropTypes.func,
  afterFilesArchived: PropTypes.func,
};

ArchiveModal.defaultProps = {
  files: [],
  collections: [],
  afterCollectionArchived: () => {},
  afterCollectionsArchived: () => {},
  afterFileArchived: () => {},
  afterFilesArchived: () => {},
};

export default connect(null, mapDispatchToProps)(injectIntl(ArchiveModal));
